/**
 * Budget-policy card: the order LiteLLM checks spend caps in — key →
 * personal → team → global — with a live meter on every level the signed-in
 * user actually has a limit on. `compact` drops the numbered rail and keeps
 * only the user's own limits; `collapsible` folds the body under a one-line
 * summary; `action` pins a host-supplied node to the card bottom.
 */
import { useId, useMemo, useState, type ReactNode } from 'react';
import { Skeleton, Text } from '@backstage/ui';
import { RiArrowDownSLine } from '@remixicon/react';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import { rootRouteRef } from '../routes';
import { buildKeysLink } from '../routeHelpers';
import { useLiteLLMProfile } from '../hooks/useLiteLLMProfile';
import { widgetViewState, UNPROVISIONED_MSG } from '../widgetState';
import { UserInfo, TeamInfo, VirtualKey } from '../types';
import { StatusPill, Tone } from './ui';
import { LimitCard, MoreKeysNote, TaggedLimit } from './BudgetLimitList';
import {
  buildBudgetSummary,
  budgetHeadline,
  BudgetLimit,
  BudgetSummary,
  budgetTone,
} from '../budget';
import styles from './LiteLLMBudgetWidget.module.css';

export interface LiteLLMBudgetWidgetProps {
  /** Card title. Defaults to 'Budget Policy'. */
  title?: string;
  /** Max key budgets to show, closest to the cap first. Defaults to 3. */
  maxKeys?: number;
  /** Show only the limits the user has, without the numbered rail. */
  compact?: boolean;
  /** Fold the body under a clickable one-line summary header. */
  collapsible?: boolean;
  /** Initial expanded state when `collapsible`. Defaults to true. */
  defaultExpanded?: boolean;
  /** Pinned below a divider, outside the collapsible region. */
  action?: ReactNode;
  /**
   * Draw the surrounding card and heading. Off inside a host that already
   * supplies them, such as the home grid — see `LiteLLMHomeWidget`.
   */
  frame?: boolean;
  /** Preloaded data (the plugin page passes its own); fetched when omitted. */
  userInfo?: UserInfo | null;
  teams?: TeamInfo[];
  keys?: VirtualKey[];
}

/** How each enforcement level reads in the collapsed summary line. */
const CLOSEST_LEVEL_LABEL: Record<BudgetLimit['kind'], string> = {
  key: 'key',
  user: 'personal',
  team: 'team',
};

const LevelFrame = ({
  rank,
  name,
  tagline,
  note,
  isLast,
  children,
}: {
  rank: number;
  name: string;
  tagline: string;
  note?: string;
  isLast?: boolean;
  children: ReactNode;
}) => (
  <div className={styles.level}>
    <div className={styles.rail} data-tone="accent">
      <span className={styles.rank}>{rank}</span>
      {!isLast && <span className={styles.connector} />}
    </div>
    <div className={isLast ? styles.levelBodyLast : styles.levelBody}>
      <div className={styles.levelHead}>
        <Text variant="body-x-small" className={styles.levelName}>
          {name}
        </Text>
        <Text variant="body-x-small" className={styles.levelTagline}>
          {tagline}
        </Text>
        {note && (
          <StatusPill
            label={note}
            tone="neutral"
            dot={false}
            className={styles.levelNote}
          />
        )}
      </div>
      <div className={styles.limits}>{children}</div>
    </div>
  </div>
);

const EmptyLimitCard = ({ children }: { children: ReactNode }) => (
  <Text variant="body-small" className={styles.emptyLimit}>
    {children}
  </Text>
);

/** The one thing that's easy to get wrong about LiteLLM budgets: the order. */
const HierarchyNote = ({ withReset }: { withReset?: boolean }) => (
  <Text variant="body-x-small" className={styles.note}>
    <b>Order:</b> key → personal → team → global. The first cap you reach blocks
    the request; a key that belongs to a team uses the team's cap, not your
    personal one.
    {withReset &&
      ' A cap with a reset window drops to $0 when the window closes; without one it never resets.'}
  </Text>
);

const FullBody = ({
  summary,
  keysHref,
}: {
  summary: BudgetSummary;
  keysHref?: string;
}) => {
  const hasBudgetedKeys = summary.keys.length + summary.hiddenBudgetedKeys > 0;
  return (
    <>
      <LevelFrame
        rank={1}
        name="Key"
        tagline="per-key cap · highest priority"
        note={hasBudgetedKeys ? undefined : 'no key budgets set'}
      >
        {!hasBudgetedKeys && (
          <EmptyLimitCard>
            No key has a budget, so no key-level cap applies.
          </EmptyLimitCard>
        )}
        {summary.keys.map(k => (
          <LimitCard key={k.sublabel ?? k.label} limit={k} />
        ))}
        {summary.hiddenBudgetedKeys > 0 && (
          <MoreKeysNote count={summary.hiddenBudgetedKeys} href={keysHref} />
        )}
      </LevelFrame>

      <LevelFrame
        rank={2}
        name="User"
        tagline="cap on everything you spend with your own keys"
        note="skipped for team-bound keys"
      >
        {summary.user ? (
          <LimitCard limit={summary.user} />
        ) : (
          <EmptyLimitCard>
            No personal budget is set on your account.
          </EmptyLimitCard>
        )}
      </LevelFrame>

      <LevelFrame
        rank={3}
        name="Team"
        tagline="shared cap for all keys in a team (if any)"
      >
        {summary.teams.length > 0 ? (
          summary.teams.map(t => (
            <LimitCard key={t.sublabel ?? t.label} limit={t} />
          ))
        ) : (
          <EmptyLimitCard>No team you belong to has a budget.</EmptyLimitCard>
        )}
      </LevelFrame>

      <LevelFrame
        rank={4}
        name="Global"
        tagline="proxy-wide cap set by your admin"
        isLast
      >
        <EmptyLimitCard>
          Configured by your admin in the proxy config — it isn't visible from
          this page. When set, it limits spend across every request on the
          proxy.
        </EmptyLimitCard>
      </LevelFrame>

      <div className={styles.noteWrap}>
        <HierarchyNote withReset />
      </div>
    </>
  );
};

const CompactBody = ({
  summary,
  keysHref,
}: {
  summary: BudgetSummary;
  keysHref?: string;
}) => {
  const { count } = budgetHeadline(summary);
  if (count === 0) {
    return (
      <Text variant="body-small" color="secondary">
        No budget limits apply to you right now.
      </Text>
    );
  }
  return (
    <div className={styles.compactList}>
      <HierarchyNote />
      {summary.keys.map(k => (
        <TaggedLimit key={k.sublabel ?? k.label} limit={k} />
      ))}
      {summary.hiddenBudgetedKeys > 0 && (
        <MoreKeysNote count={summary.hiddenBudgetedKeys} href={keysHref} />
      )}
      {summary.user && <TaggedLimit limit={summary.user} />}
      {summary.teams.map(t => (
        <TaggedLimit key={t.sublabel ?? t.label} limit={t} />
      ))}
    </div>
  );
};

const LoadingRail = () => (
  <div className={styles.skeletons}>
    {[0, 1, 2, 3].map(i => (
      <div key={i} className={styles.skeletonRow}>
        <Skeleton width={24} height={24} rounded />
        <div className={styles.skeletonLines}>
          <Skeleton width="60%" height={14} />
          <Skeleton width="100%" height={8} />
          <Skeleton width="40%" height={10} />
        </div>
      </div>
    ))}
  </div>
);

export const LiteLLMBudgetWidget = ({
  title = 'Budget Policy',
  maxKeys = 3,
  compact = false,
  collapsible = false,
  defaultExpanded = true,
  action,
  frame = true,
  userInfo: propUserInfo,
  teams: propTeams,
  keys: propKeys,
}: LiteLLMBudgetWidgetProps) => {
  const profile = useLiteLLMProfile();
  // Resolved, not hardcoded: page:litellm's path is overridable in app-config.
  const routeLink = useRouteRef(rootRouteRef);
  const keysHref = buildKeysLink(routeLink?.());
  const [expanded, setExpanded] = useState(defaultExpanded);
  const bodyId = useId();

  // The page hands over what it already loaded; the profile hook still runs
  // (hooks can't be conditional) but its cache makes that free.
  const preloaded = propUserInfo !== undefined;
  const user = preloaded ? propUserInfo : profile.userInfo;
  const teams = propTeams ?? profile.teams;
  const keys = propKeys ?? profile.keys;

  const viewState = useMemo(
    () =>
      widgetViewState({
        loading: preloaded ? false : profile.loading,
        error: preloaded ? null : profile.error,
        userInfo: user,
        usageError: null,
        hasKeys: keys.length > 0,
      }),
    [preloaded, profile.loading, profile.error, user, keys],
  );

  const summary = useMemo(
    () => buildBudgetSummary(user, teams, keys, maxKeys),
    [user, teams, keys, maxKeys],
  );
  const headline = useMemo(() => budgetHeadline(summary), [summary]);

  const summaryText = (() => {
    if (headline.count === 0) return 'no limits apply';
    const limits = `${headline.count} limit${headline.count === 1 ? '' : 's'}`;
    if (!headline.closest) return limits;
    return `${limits} · closest: ${
      CLOSEST_LEVEL_LABEL[headline.closest.kind]
    } ${Math.round(headline.closest.pct)}%`;
  })();
  const summaryTone: Tone = budgetTone(headline.closest?.pct ?? 0);

  const renderBody = () => {
    switch (viewState.kind) {
      case 'loading':
        return <LoadingRail />;
      // Quiet, generic copy: raw backend errors never reach a widget.
      case 'error':
        return (
          <Text variant="body-small" className={styles.quiet}>
            {viewState.message}
          </Text>
        );
      case 'unprovisioned':
        return (
          <Text variant="body-small" className={styles.quiet}>
            {UNPROVISIONED_MSG}
          </Text>
        );
      default:
        return compact ? (
          <CompactBody summary={summary} keysHref={keysHref} />
        ) : (
          <FullBody summary={summary} keysHref={keysHref} />
        );
    }
  };

  const renderHead = () => {
    if (collapsible) {
      return (
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setExpanded(e => !e)}
          aria-expanded={expanded}
          aria-controls={bodyId}
          aria-label={
            expanded ? 'Collapse budget policy' : 'Expand budget policy'
          }
        >
          <span className={styles.toggleText}>
            {frame && <Text variant="title-x-small">{title}</Text>}
            {viewState.kind === 'ready' && (
              <StatusPill label={summaryText} tone={summaryTone} />
            )}
          </span>
          <RiArrowDownSLine
            size={18}
            className={styles.chevron}
            data-expanded={expanded}
            aria-hidden
          />
        </button>
      );
    }
    if (!frame) return null;
    return (
      <div className={styles.head}>
        <Text variant="title-x-small">{title}</Text>
        <Text variant="body-x-small" className={styles.subtitle}>
          How LiteLLM caps spend — and where you stand right now
        </Text>
      </div>
    );
  };

  const head = renderHead();
  const body = (
    <>
      {head}
      {(!collapsible || expanded) && (
        <div id={bodyId} className={head ? styles.body : undefined}>
          {renderBody()}
        </div>
      )}
      {action && <div className={styles.action}>{action}</div>}
    </>
  );

  return frame ? <div className={styles.card}>{body}</div> : body;
};
